import React, { useState, useEffect, useImperativeHandle, useCallback, forwardRef } from 'react';
import { Button, Drawer, AutoComplete, Row, Col, Collapse, Popover, Checkbox, Space, Switch } from 'antd';
import { Filter, Location, ChevronDown, CloseIcon, Search, ChevronUp, Minus, Plus, GoBack, } from '../../assets/icons';
import { CloseCircleOutlined } from '@ant-design/icons';

import './SearchFilter.scss';
import { debounce } from '../../utils';
import { getMarketPlaceSearch, getPropertTypeData, getSearchHistoryData } from '../../services/FiltersApi/filters';
import { toastNotify } from '../../utils/toastNotify';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const { Panel } = Collapse;

const FILTER_NAME = {
  LOCATION: 'location',
  PROPERTY_TYPE: 'property type',
  SORT_BY: 'sort by',
};

const FavSearchFilter = forwardRef((props, ref) => {
  const { totalProperties = 0, onSearchOrFilter = () => { } } = props;
  const [drawerOpen, setDrawerOpen] = useState();
  const [openFilter, setOpenFilter] = useState('');
  const [isScroll, setIsScroll] = useState(false);

  // const screenMD = useMediaQuery('(min-width: 1000px)');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState([]);
  const [propertyType, setPropertyType] = useState([]);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    owned: false,
  });

  const fetchInitialSearchOptions = async () => {
    try {
      setIsFetchingInitialData(true);
      const searchHistoryData = await getSearchHistoryData();
      setSearchHistory(searchHistoryData?.data?.searchHistory);
      const propertyTypeData = await getPropertTypeData({ isFavorite: true });
      setPropertyType(propertyTypeData?.data?.propertyTypes);
    } catch (err) {
      toastNotify('error', 'Error in fetching initial serach options');
    } finally {
      setIsFetchingInitialData(false);
    }
  };
  useEffect(() => {
    window.addEventListener('scroll', () => {
      setIsScroll(window.scrollY > 120);
    });
    return () => {
      window.removeEventListener('scroll', () => { });
    };
  }, []);

  useEffect(() => {
    fetchInitialSearchOptions();
  }, []);

  const handleOnRemovePropertyTypeFilter = () => {
    setSelectedFilters({
      owned: false,
    });
    if (searchQuery.trim()) {
      onSearchOrFilter({ searchQuery });
    } else {
      onSearchOrFilter({});
    }
  };
  const handleOnRemoveAllFilter = () => {
    setSearchQuery('');
    setSelectedFilters({
      owned: false,
    });
    onSearchOrFilter({});
  };
  useImperativeHandle(
    ref,
    () => {
      return {
        handleOnRemovePropertyTypeFilter,
        handleOnRemoveAllFilter,
        searchQuery,
        selectedFilters,
      };
    },
    [searchQuery, selectedFilters]
  );

  const onHandleSearch = async (value) => {
    if (value.trim()) {
      const data = await getMarketPlaceSearch({ search: value, onFail: () => { } });
      setSearchOptions(
        data?.data?.searchResult?.map((property, i) => {
          return searchRecommendation({ ...property }, i);
        })
      );
    } else {
      setSearchOptions(
        searchHistory?.map((item) => {
          return {
            key: item?.id,
            label: (
              <>
                <GoBack />
                <span>{item?.searchKey}</span>
              </>
            ),
            value: item?.searchKey,
          };
        })
      );
    }
  };

  const onHandleFocus = async (e) => {
    if (!e.target.value.trim()) {
      setSearchOptions(
        searchHistory?.map((item) => {
          return {
            key: item?.id,
            label: (
              <>
                <GoBack />
                <span>{item?.searchKey}</span>
              </>
            ),
            value: item?.searchKey,
          };
        })
      );
    }
    return;
  };

  const onSearch = useCallback(debounce(onHandleSearch), [searchHistory]);
  const onFocus = useCallback(debounce(onHandleFocus), [searchHistory]);

  const searchRecommendation = (props, index) => {
    const icons = [];
    const labels = [];
    if (props?.property) {
      icons.push(<LazyLoadImage key="property-icon" alt={''} className='mr-8' width='20px' height='20px' src={props?.propertyTypeImageUrl} />);
      labels.push(<span key="property-label" className='fw-600'>{props?.property}</span>);
    }
    if (props?.city) {
      icons.push(<Location key="city-icon" verticalAlign='-8px' />);
      labels.push(<span key="city-label">{`city: ${props?.city}, ${props?.stateCode}`}</span>);
    }
    if (props?.state) {
      icons.push(<Location key="state-icon" verticalAlign='-8px' />);
      labels.push(<span key="state-label">{`state: ${props?.state}`}</span>);
    }
    return {
      key: props?.property + index,
      label: (
        <>
          {icons}
          {labels.map((label, idx) => (
            <span key={`label-${idx}`} className='mr-2'>{label}</span>
          ))}
        </>
      ),
      value: icons.length > 0 ? labels.map(label => label.props.children.replace('city: ', '').replace('state: ', '')).join(', ').split(',')[0] : '',
    };
  };
  const handleOnSearchFilterSelect = (operation, value) => {
    const data = {};
    let propertyTypes = [];
    const { owned, ...selectedTypes } = selectedFilters;
    switch (operation) {
      case 'applyAll':
        setDrawerOpen(null);
        if (searchQuery.trim()) {
          data['searchQuery'] = searchQuery;
        }
        Object.keys(selectedTypes).forEach((typeId) => {
          if (selectedTypes?.[typeId]) {
            propertyTypes.push(typeId);
          }
        });
        if (propertyTypes.length) {
          data['propertyTypes'] = propertyTypes;
        }
        if (owned) {
          data['owned'] = 1;
        }
        break;
      case 'search':
        setSearchQuery(value);
        if (value.trim()) {
          data['searchQuery'] = value;
        }
        Object.keys(selectedTypes).forEach((typeId) => {
          if (selectedTypes?.[typeId]) {
            propertyTypes.push(typeId);
          }
        });
        if (propertyTypes.length) {
          data['propertyTypes'] = propertyTypes;
        }
        if (owned) {
          data['owned'] = 1;
        }
        break;
      case 'filter':
        setSelectedFilters((state) => {
          return {
            ...state,
            ...value,
          };
        });

        if (searchQuery.trim()) {
          data['searchQuery'] = searchQuery;
        }
        Object.keys(selectedTypes).forEach((typeId) => {
          if (selectedTypes?.[typeId]) {
            propertyTypes.push(typeId);
          }
        });
        if (value[Object.keys(value)?.[0]]) {
          propertyTypes.push(Object.keys(value)?.[0]);
        } else {
          propertyTypes = propertyTypes.filter((x) => x !== Object.keys(value)?.[0]);
        }
        if (propertyTypes.length) {
          data['propertyTypes'] = propertyTypes;
        }
        if (owned) {
          data['owned'] = 1;
        }

        break;
      case 'owned':
        setSelectedFilters((state) => ({
          ...state,
          owned: value,
        }));
        if (searchQuery.trim()) {
          data['searchQuery'] = searchQuery;
        }
        Object.keys(selectedTypes).forEach((typeId) => {
          if (selectedTypes?.[typeId]) {
            propertyTypes.push(typeId);
          }
        });
        if (propertyTypes.length) {
          data['propertyTypes'] = propertyTypes;
        }
        if (value) {
          data['owned'] = 1;
        }
        break;
      default:
        break;
    }
    onSearchOrFilter(data);
  };

  return (
    <>
      <div className={isScroll ? 'filterbar-wrapper sticky' : 'filterbar-wrapper'}>
        <div className='container-fluid filterbar'>
          <p className='w-100 filterbar__title'>{props.page}</p>
          <Button
            className='filterbar__trigger--mobile'
            icon={<Filter size={18} verticalAlign='-3px' marginRight='8px' />}
            onClick={() => setDrawerOpen('mobile')}
            loading={isFetchingInitialData}
          >
            Filters
          </Button>
          <div id='search-search'></div>
          {/* <div id='type-search'></div> */}
          <div className='filterbar__search filterbar__search--desktop'>
            <AutoComplete
              allowClear
              disabled={isFetchingInitialData}
              popupClassName='filterbar__search__dropdown'
              getPopupContainer={() => document.getElementById('search-search')}
              dropdownMatchSelectWidth={false}
              clearIcon={<span className='clear'>clear</span>}
              options={searchOptions}
              placeholder='Search by Name, City, State'
              onSearch={onSearch}
              onFocus={onFocus}
              onChange={(value) => {
                if (value === undefined) {
                  // Handle the clear icon click
                  handleOnSearchFilterSelect('search', '');
                } else if (searchQuery.trim() && !value?.trim()) {
                  // Handle case when searchQuery exists but value is empty or only spaces
                  handleOnSearchFilterSelect('search', value);
                } else {
                  // Update the search query
                  setSearchQuery(value);
                }                
              }}
              onSelect={(value) => {
                if (value === undefined) {
                  // Handle the clear icon click
                  handleOnSearchFilterSelect('search', '');
                } else {
                  // Handle selection
                  handleOnSearchFilterSelect('search', value);
                }
              }}
              value={searchQuery}
              suffixIcon={<CloseCircleOutlined onClick={() => setSearchQuery('')} />}
            />
            <Search className='search-icon' size={18} />
          </div>

          <Popover
            open={openFilter === FILTER_NAME.PROPERTY_TYPE}
            className='filterbar__type__trigger'
            popupClassName='filterbar__type'
            getPopupContainer={() => document.getElementById('search-search')}
            content={
              <>
                <Space direction='vertical' size={20} className='filterbar__type__options'>
                  {React.Children.toArray(
                    propertyType?.map((item) => {
                      return (
                        <Checkbox
                          onChange={(e) => {
                            handleOnSearchFilterSelect('filter', {
                              [item?.propertyTypeId]: e.target.checked,
                            });
                          }}
                          checked={Boolean(selectedFilters?.[item?.propertyTypeId])}
                        >
                          {item?.name || ''} <span className='text-taupe-80'>({item?.propertyCount || 0})</span>
                        </Checkbox>
                      );
                    })
                  )}
                </Space>
                <div className='px-24'>
                  <hr />
                  <div className='py-24'>
                    <Switch
                      checked={Boolean(selectedFilters?.owned)}
                      onChange={(e) => {
                        handleOnSearchFilterSelect('owned', e);
                      }}
                    />{' '}
                    <span className='fz-16 ml-8 switch-lbl'>Properties I own</span>
                  </div>
                </div>
              </>
            }
            placement='bottomLeft'
            trigger='click'
            onOpenChange={() => setOpenFilter(openFilter === FILTER_NAME.PROPERTY_TYPE ? '' : FILTER_NAME.PROPERTY_TYPE)}
          >
            <Button className='ml-8 btn-small'>
              Property type{' '}
              {openFilter === FILTER_NAME.PROPERTY_TYPE ? (
                <ChevronUp verticalAlign='-4px' size={18} marginLeft='2px' />
              ) : (
                <ChevronDown verticalAlign='-4px' size={18} marginLeft='2px' />
              )}
            </Button>
          </Popover>

          <p className='property-count'>{`${totalProperties} properties`}</p>
        </div>
        <Drawer
          className='filter__drawer'
          open={drawerOpen === 'mobile'}
          footer={
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  className='w-100 p-0'
                  onClick={() => {
                    setDrawerOpen(null);
                    handleOnRemoveAllFilter();
                  }}
                >
                  Reset All
                </Button>
              </Col>
              <Col span={12}>
                <Button className='w-100 p-0' type='primary' onClick={() => handleOnSearchFilterSelect('applyAll')}>
                  Apply
                </Button>
              </Col>
            </Row>
          }
          onClose={() => setDrawerOpen(null)}
          title={<h5 className='mb-0 text-center fz-20 fw-700'>All filters</h5>}
          closeIcon={<CloseIcon />}
        >
          <div className='filterbar__search filterbar__search--mobile'>
            <Search className='search-icon' size={18} />
            <AutoComplete
              className='w-100'
              allowClear
              disabled={isFetchingInitialData}
              dropdownMatchSelectWidth={false}
              clearIcon={<span className='clear'>clear</span>}
              options={searchOptions}
              placeholder='Search by Name, City, State'
              onSearch={onSearch}
              onFocus={onFocus}
              onChange={(value) => setSearchQuery(value)}
              value={searchQuery}
            />
          </div>

          <div className='filter-options mt-24'>
            {/* <div className='filter-options__item'>
              <Switch /> <span className='ml-8'>Saved properties</span>
            </div> */}
            <div className='filter-options__item'>
              <Switch checked={Boolean(selectedFilters?.owned)} onChange={(e) => setSelectedFilters((state) => ({ ...state, owned: e }))} />{' '}
              <span className='ml-8'>Properties I own</span>
            </div>
            {/* <div className='filter-options__item'>
              <Switch /> <span className='ml-8'>Currently paying dividend</span>
            </div> */}
          </div>

          <hr className='hr' />

          <Collapse
            bordered={false}
            expandIconPosition='end'
            expandIcon={({ isActive }) => (isActive ? <Minus marginRight='-15px' /> : <Plus marginRight='-15px' />)}
          >
            <Panel header={<p className='subhead-3 mb-0'>Property type</p>} key='2'>
              <Space direction='vertical' size={24} className='filter-options'>
                {React.Children.toArray(
                  propertyType?.map((item) => {
                    return (
                      <Checkbox
                        onChange={(e) => setSelectedFilters((state) => ({ ...state, [item?.propertyTypeId]: e.target.checked }))}
                        checked={Boolean(selectedFilters?.[item?.propertyTypeId])}
                      >
                        {item?.name || ''} <span className='text-taupe-80'>({item?.propertyCount || 0})</span>
                      </Checkbox>
                    );
                  })
                )}
              </Space>
            </Panel>
          </Collapse>
        </Drawer>
      </div>
    </>
  );
});

export default FavSearchFilter;
